import React, { useState, useEffect } from 'react';
import { Modal } from '../../components/ui/Modal';
import { Button, Select } from '../../components/ui/index.jsx';
import { Plus, Trash2, Clock, Calendar, Copy, LogIn, LogOut } from 'lucide-react';
import api from '../../utils/api';
import { useToast } from '../../contexts/ToastContext';

const DAYS = ['الاثنين', 'الثلاثاء', 'الأربعاء', 'الخميس', 'الجمعة', 'السبت'];

const MORNING_ENTRY = [
    { value: '08:00', label: '08H00 (دخول 08:00)' },
    { value: '09:00', label: '09H00 (دخول 09:00)' },
    { value: '10:00', label: '10H00 (دخول 10:00)' },
];

const AFTERNOON_ENTRY = [
    { value: '14:00', label: '14H00 (دخول 14:00)' },
    { value: '15:00', label: '15H00 (دخول 15:00)' },
    { value: '16:00', label: '16H00 (دخول 16:00)' },
];

const MORNING_EXIT = [
    { value: '10:00', label: '10H00 (خروج 10:00)' },
    { value: '11:00', label: '11H00 (خروج 11:00)' },
    { value: '12:00', label: '12H00 (خروج 12:00)' },
];

const AFTERNOON_EXIT = [
    { value: '16:00', label: '16H00 (خروج 16:00)' },
    { value: '17:00', label: '17H00 (خروج 17:00)' },
    { value: '18:00', label: '18H00 (خروج 18:00)' },
];

const PRESETS = [
    { label: '08H00 ➔ 12H00 (صباح كامل)', start: '08:00', end: '12:00', notes: 'الفترة الصباحية' },
    { label: '08H00 ➔ 10H00 (صباح 1)', start: '08:00', end: '10:00', notes: 'صباحاً' },
    { label: '10H00 ➔ 12H00 (صباح 2)', start: '10:00', end: '12:00', notes: 'صباحاً' },
    { label: '09H00 ➔ 12H00 (3 ساعات)', start: '09:00', end: '12:00', notes: 'صباحاً' },
    { label: '14H00 ➔ 18H00 (مساء كامل)', start: '14:00', end: '18:00', notes: 'الفترة المسائية' },
    { label: '14H00 ➔ 16H00 (مساء 1)', start: '14:00', end: '16:00', notes: 'مساءً' },
    { label: '16H00 ➔ 18H00 (مساء 2)', start: '16:00', end: '18:00', notes: 'مساءً' },
    { label: '15H00 ➔ 18H00 (3 ساعات)', start: '15:00', end: '18:00', notes: 'مساءً' },
];

export const TimetableModal = ({ isOpen, onClose, onSuccess, initialClass = null }) => {
    const { success, error: toastError } = useToast();
    const [selectedClassId, setSelectedClassId] = useState('');
    const [slots, setSlots] = useState([]);
    const [isSubmitting, setIsSubmitting] = useState(false);

    // Load all school classes directly - independent of timetables data
    const [allClasses, setAllClasses] = useState([]);
    const [classesLoading, setClassesLoading] = useState(false);

    useEffect(() => {
        if (isOpen) {
            fetchAllClasses();
        }
    }, [isOpen]);

    const fetchAllClasses = async () => {
        setClassesLoading(true);
        try {
            const res = await api.get('/school-classes?all=true');
            const raw = res.data;
            let data = [];
            if (Array.isArray(raw)) {
                data = raw;
            } else if (Array.isArray(raw?.data)) {
                data = raw.data;
            }
            setAllClasses(data);
        } catch (err) {
            console.error('Failed to load school classes', err);
        } finally {
            setClassesLoading(false);
        }
    };

    useEffect(() => {
        if (isOpen) {
            if (initialClass) {
                setSelectedClassId(String(initialClass.id));
                if (initialClass.timetables && initialClass.timetables.length > 0) {
                    setSlots(initialClass.timetables.map(t => ({
                        day_of_week: t.day_of_week,
                        start_time: t.start_time ? t.start_time.substring(0, 5) : '08:00',
                        end_time: t.end_time ? t.end_time.substring(0, 5) : '12:00',
                        notes: t.notes || ''
                    })));
                } else {
                    setSlots([]);
                }
            } else {
                setSelectedClassId('');
                setSlots([]);
            }
        }
    }, [isOpen, initialClass]);

    const classOptions = allClasses.map(cls => ({
        value: String(cls.id),
        label: cls.name
    }));

    const handleClassChange = (e) => {
        setSelectedClassId(e.target.value);
        setSlots([]);
    };

    const handleAddSlot = (day, start_time = '08:00', end_time = '12:00', notes = '') => {
        setSlots(prev => [...prev, { day_of_week: day, start_time, end_time, notes }]);
    };

    const handleRemoveSlot = (index) => {
        setSlots(prev => prev.filter((_, i) => i !== index));
    };

    const handleSlotChange = (index, field, value) => {
        setSlots(prev => prev.map((slot, i) => i === index ? { ...slot, [field]: value } : slot));
    };

    const handleCopyDaySlots = (sourceDay) => {
        const daySlots = slots.filter(s => s.day_of_week === sourceDay);
        if (daySlots.length === 0) {
            toastError(`لا توجد فترات مضافة في يوم ${sourceDay} لنسخها`);
            return;
        }
        
        let newSlots = [...slots];
        DAYS.forEach(targetDay => {
            if (targetDay !== sourceDay) {
                newSlots = newSlots.filter(s => s.day_of_week !== targetDay);
                daySlots.forEach(s => {
                    newSlots.push({ ...s, day_of_week: targetDay });
                });
            }
        });
        setSlots(newSlots);
        success(`تم نسخ توقيت يوم ${sourceDay} لجميع باقي الأيام بنجاح`);
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        if (!selectedClassId) {
            toastError('يرجى اختيار القسم الدراسي');
            return;
        }
        setIsSubmitting(true);
        try {
            await api.post('/timetables', { school_class_id: selectedClassId, slots });
            success('تم حفظ استعمال الزمن بنجاح');
            onSuccess();
            onClose();
        } catch (err) {
            toastError(err?.response?.data?.message || 'خطأ أثناء حفظ استعمال الزمن');
        } finally {
            setIsSubmitting(false);
        }
    };

    return (
        <Modal
            isOpen={isOpen}
            onClose={onClose}
            title={initialClass ? `تعديل استعمال الزمن — ${initialClass.name}` : 'إضافة استعمال زمن لـ قسم'}
            size="full"
            noShadow={true}
        >
            <form onSubmit={handleSubmit} className="space-y-5 h-full flex flex-col justify-between">
                {/* Class Selector */}
                <div>
                    {classesLoading ? (
                        <div className="space-y-1.5">
                            <label className="text-xs text-slate-800 ml-1">القسم الدراسي <span className="text-red-500">*</span></label>
                            <div className="w-full bg-slate-50 border border-slate-200 rounded-lg py-2.5 px-4 text-xs text-slate-400 animate-pulse">
                                جاري تحميل الأقسام...
                            </div>
                        </div>
                    ) : (
                        <Select
                            label={<>القسم الدراسي <span className="text-red-500">*</span></>}
                            options={classOptions}
                            value={selectedClassId}
                            onChange={handleClassChange}
                            placeholder="ابحث أو اختر القسم..."
                            disabled={!!initialClass}
                        />
                    )}
                </div>

                {/* Days list */}
                <div className="space-y-4 overflow-y-auto pr-1 flex-1">
                    {DAYS.map(day => {
                        const daySlots = slots.filter(s => s.day_of_week === day);
                        return (
                            <div key={day} className="border border-slate-200 rounded-xl overflow-hidden bg-white">
                                {/* Day header */}
                                <div className="flex flex-wrap items-center justify-between gap-2 px-4 py-3 bg-slate-50 border-b border-slate-200">
                                    <h4 className="font-bold text-sm text-slate-800 flex items-center gap-2">
                                        <Calendar className="w-4 h-4 text-primary" />
                                        {day}
                                        {daySlots.length > 0 ? (
                                            <span className="text-[11px] font-bold bg-primary/10 text-primary px-2.5 py-0.5 rounded-full">
                                                {daySlots.length} فترة
                                            </span>
                                        ) : (
                                            <span className="text-[11px] text-slate-400 font-normal">
                                                (بدون حصص)
                                            </span>
                                        )}
                                    </h4>

                                    <div className="flex items-center gap-1.5 flex-wrap">
                                        {daySlots.length > 0 && (
                                            <button
                                                type="button"
                                                onClick={() => handleCopyDaySlots(day)}
                                                className="px-2.5 py-1 text-[11px] font-bold rounded-lg border border-slate-200 bg-white text-slate-600 hover:border-emerald-500 hover:text-emerald-600 hover:bg-emerald-50 transition-all flex items-center gap-1"
                                                title="نسخ توقيت هذا اليوم لجميع الأيام الأخرى"
                                            >
                                                <Copy className="w-3 h-3" /> نسخ لباقي الأيام
                                            </button>
                                        )}
                                        <button
                                            type="button"
                                            onClick={() => handleAddSlot(day)}
                                            className="px-2.5 py-1 text-[11px] font-bold rounded-lg bg-primary text-white hover:bg-primary/90 transition-all flex items-center gap-1"
                                        >
                                            <Plus className="w-3 h-3" /> إضافة فترة
                                        </button>
                                    </div>
                                </div>

                                 {/* Slots list for this day */}
                                <div className="divide-y divide-slate-100">
                                    {daySlots.length === 0 ? (
                                        <p className="text-xs text-slate-400 italic text-center py-4 bg-white">
                                            لا توجد حصص مضافة لهذا اليوم — اضغط على زر "إضافة فترة" أعلاه
                                        </p>
                                    ) : (
                                        slots.map((slot, index) => {
                                            if (slot.day_of_week !== day) return null;
                                            return (
                                                <div key={index} className="flex flex-wrap items-center gap-3 px-4 py-3 bg-white hover:bg-slate-50/50 transition-colors">
                                                    
                                                    {/* Entry (دخول) */}
                                                    <div className="flex items-center gap-1.5 bg-emerald-50 border border-emerald-200 rounded-lg px-2.5 py-1">
                                                        <LogIn className="w-3.5 h-3.5 text-emerald-600" />
                                                        <span className="text-xs font-bold text-emerald-800">دخول:</span>
                                                        <select
                                                            value={slot.start_time}
                                                            onChange={(e) => handleSlotChange(index, 'start_time', e.target.value)}
                                                            className="bg-white border border-emerald-300 rounded px-2 py-1 text-xs font-bold text-slate-900 focus:outline-none focus:ring-1 focus:ring-emerald-500 cursor-pointer"
                                                            required
                                                        >
                                                            <optgroup label="الفترة الصباحية (دخول)">
                                                                {MORNING_ENTRY.map(opt => (
                                                                    <option key={opt.value} value={opt.value}>{opt.label}</option>
                                                                ))}
                                                            </optgroup>
                                                            <optgroup label="الفترة المسائية (دخول)">
                                                                {AFTERNOON_ENTRY.map(opt => (
                                                                    <option key={opt.value} value={opt.value}>{opt.label}</option>
                                                                ))}
                                                            </optgroup>
                                                            {![...MORNING_ENTRY, ...AFTERNOON_ENTRY].some(o => o.value === slot.start_time) && (
                                                                <option value={slot.start_time}>{slot.start_time}</option>
                                                            )}
                                                        </select>
                                                    </div>

                                                    <span className="text-slate-300 font-bold">➔</span>

                                                    {/* Exit (خروج) */}
                                                    <div className="flex items-center gap-1.5 bg-amber-50 border border-amber-200 rounded-lg px-2.5 py-1">
                                                        <LogOut className="w-3.5 h-3.5 text-amber-600" />
                                                        <span className="text-xs font-bold text-amber-800">خروج:</span>
                                                        <select
                                                            value={slot.end_time}
                                                            onChange={(e) => handleSlotChange(index, 'end_time', e.target.value)}
                                                            className="bg-white border border-amber-300 rounded px-2 py-1 text-xs font-bold text-slate-900 focus:outline-none focus:ring-1 focus:ring-amber-500 cursor-pointer"
                                                            required
                                                        >
                                                            <optgroup label="الفترة الصباحية (خروج)">
                                                                {MORNING_EXIT.map(opt => (
                                                                    <option key={opt.value} value={opt.value}>{opt.label}</option>
                                                                ))}
                                                            </optgroup>
                                                            <optgroup label="الفترة المسائية (خروج)">
                                                                {AFTERNOON_EXIT.map(opt => (
                                                                    <option key={opt.value} value={opt.value}>{opt.label}</option>
                                                                ))}
                                                            </optgroup>
                                                            {![...MORNING_EXIT, ...AFTERNOON_EXIT].some(o => o.value === slot.end_time) && (
                                                                <option value={slot.end_time}>{slot.end_time}</option>
                                                            )}
                                                        </select>
                                                    </div>

                                                    {/* Remove */}
                                                    <button
                                                        type="button"
                                                        onClick={() => handleRemoveSlot(index)}
                                                        className="text-slate-400 hover:text-red-600 transition-colors p-1.5 rounded-lg hover:bg-red-50"
                                                        title="حذف هذه الفترة"
                                                    >
                                                        <Trash2 className="w-4 h-4" />
                                                    </button>
                                                </div>
                                            );
                                        })
                                    )}
                                </div>
                            </div>
                        );
                    })}
                </div>

                {/* Footer */}
                <div className="flex justify-end gap-3 border-t border-slate-100 pt-4">
                    <Button type="button" variant="outline" onClick={onClose}>
                        إلغاء
                    </Button>
                    <Button type="submit" isLoading={isSubmitting}>
                        حفظ استعمال الزمن
                    </Button>
                </div>
            </form>
        </Modal>
    );
};
